import { useEffect, useRef } from 'react';
import { EventType, RealtimeMessage } from '@inplay/models';
import { useWebSocket } from '../service/context/websocketContext';
import { validateAndDecodeAuthToken } from '../utils/helper';
import { AuthTokenResponse } from '../service/service.type';

const EVENT_DEBOUNCE_MS: number = 100;

export const useWebsocketSubscription = (
  organizationUnitId: string | undefined,
  eventTypes: EventType[],
  onEvent: () => void,
): void => {
  const websocket = useWebSocket();
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const onEventRef = useRef<() => void>(onEvent);
  onEventRef.current = onEvent;
  const eventTypesKey: string = eventTypes.join(',');

  useEffect(() => {
    const decodedData: AuthTokenResponse | null = validateAndDecodeAuthToken();
    if (decodedData && organizationUnitId && websocket.status === 'connected') {
      eventTypes.forEach((eventType: EventType): void => {
        websocket.service.sendMessage({
          eventType,
          organizationUnitId,
          userId: decodedData.sub,
        });
      });
    }
    // eventTypesKey mirrors eventTypes contents; used as the stable dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationUnitId, websocket.status, websocket.service, eventTypesKey]);

  useEffect(() => {
    const subscribedEventTypes: Set<EventType> = new Set(eventTypes);
    const unsubscribe = websocket.service.onMessage(
      (data: RealtimeMessage): void => {
        const incomingEventType: EventType = data.eventType as EventType;
        if (subscribedEventTypes.has(incomingEventType)) {
          if (debounceRef.current) clearTimeout(debounceRef.current);
          debounceRef.current = setTimeout((): void => {
            onEventRef.current();
          }, EVENT_DEBOUNCE_MS);
        }
      },
    );

    return (): void => {
      unsubscribe();
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eventTypesKey mirrors eventTypes contents; used as the stable dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [websocket.service, eventTypesKey]);
};
